import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { CURRENCY_SYMBOLS } from "../currency";


/**
 * Assign one or more employees to multiple selected tasks at once.
 *
 * Each employee row has:
 *   - a checkbox (include?)
 *   - an optional fixed-amount input (null = inherit project rule)
 *
 * On save, posts to /tasks/batch-assign, which upserts each (task, employee)
 * pair across all selected tasks. Existing assignments on those tasks are
 * preserved (and updated if the same employee is included with a new amount).
 *
 * Props:
 *   open
 *   taskIds       : number[]   — the selected tasks
 *   currency      : sheet currency
 *   allEmployees  : list of project employees
 *   memberships   : list of {user_id, default_percentage}
 *   onClose
 *   onSaved
 */
export default function BatchAssignModal({
  open,
  taskIds = [],
  currency = "USD",
  allEmployees = [],
  memberships = [],
  onClose,
  onSaved,
}) {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const symbol = CURRENCY_SYMBOLS[currency] || "$";

  useEffect(() => {
    if (!open) {
      setRows({});
      setErr(null);
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open) return null;

  const toggle = (uid) => {
    setRows((r) => ({
      ...r,
      [uid]: {
        included: !(r[uid]?.included),
        fixedAmount: r[uid]?.fixedAmount || "",
      },
    }));
  };

  const setAmt = (uid, value) => {
    setRows((r) => ({
      ...r,
      [uid]: {
        included: r[uid]?.included ?? false,
        fixedAmount: value,
      },
    }));
  };

  const membershipFor = (uid) => memberships.find((m) => m.user_id === uid);

  const handleSave = async () => {
    const assignments = Object.entries(rows)
      .filter(([, v]) => v.included)
      .map(([uid, v]) => ({
        user_id: Number(uid),
        fixed_amount:
          v.fixedAmount === "" || v.fixedAmount === null
            ? null
            : Number(v.fixedAmount),
      }));
    if (assignments.length === 0) {
      setErr("Pick at least one employee.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.post("/tasks/batch-assign", { task_ids: taskIds, assignments });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 580 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>Batch assign</h3>
        <p className="muted" style={{ marginTop: 4, marginBottom: 16 }}>
          Adding selected employees to{" "}
          <strong style={{ color: "var(--ink)" }}>
            {taskIds.length} task{taskIds.length === 1 ? "" : "s"}
          </strong>
          . Existing assignments on those tasks are preserved; if an employee is
          already on a task, their fixed amount updates to the new value.
        </p>

        {allEmployees.length === 0 ? (
          <div className="muted-sm">
            No employees on this project. Add some on the project page first.
          </div>
        ) : (
          <div className="assignment-list">
            {allEmployees.map((emp) => {
              const row = rows[emp.id] || { included: false, fixedAmount: "" };
              const pct = membershipFor(emp.id)?.default_percentage;
              const hint = row.fixedAmount
                ? `Fixed ${symbol}${row.fixedAmount}`
                : pct != null
                ? `Project rule: ${pct}%`
                : "No pay rule";
              return (
                <div key={emp.id} className="assignment-row">
                  <label className="check-row" style={{ flex: 1 }}>
                    <input
                      type="checkbox"
                      className="cbx"
                      checked={row.included}
                      onChange={() => toggle(emp.id)}
                    />
                    <span style={{ fontWeight: 500 }}>{emp.name}</span>
                  </label>
                  <div className="assignment-hint muted-sm">{hint}</div>
                  <div className="amount-input-wrap">
                    <span className="amount-prefix">{symbol}</span>
                    <input
                      type="number"
                      step="1"
                      placeholder="fixed"
                      value={row.fixedAmount}
                      onChange={(e) => setAmt(emp.id, e.target.value)}
                      disabled={!row.included}
                      title="Optional fixed payout (overrides project %)"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary" onClick={handleSave} disabled={busy}>
            {busy ? "Assigning…" : `Assign to ${taskIds.length} task${taskIds.length === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
